/**
 * VoiceCursor
 * Sesli agent aktifken ekranda beliren, hedeflere kayıp "tıklayan" sanal imleç.
 * Imperative kontrol için küçük bir zustand store + yardımcı fonksiyonlar.
 */
import { create } from 'zustand';
import { MousePointer2 } from 'lucide-react';

type CursorState = {
  visible: boolean;
  x: number;
  y: number;
  clicking: boolean;
  label: string;
};

const useCursorStore = create<CursorState>(() => ({
  visible: false,
  x: typeof window !== 'undefined' ? window.innerWidth - 120 : 0,
  y: typeof window !== 'undefined' ? window.innerHeight - 120 : 0,
  clicking: false,
  label: '',
}));

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function showCursor(): void {
  useCursorStore.setState({ visible: true });
}

export function hideCursor(): void {
  useCursorStore.setState({ visible: false, clicking: false, label: '' });
}

export function setCursorLabel(label: string): void {
  useCursorStore.setState({ label });
}

/** Imleci verilen koordinata kaydır (CSS transition animasyonu). */
export async function moveCursorTo(x: number, y: number, duration = 600): Promise<void> {
  useCursorStore.setState({ visible: true, x, y });
  await sleep(duration);
}

/** Imleci bir DOM elementinin merkezine kaydır. */
export async function moveCursorToElement(el: HTMLElement | null, duration = 600): Promise<boolean> {
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  await sleep(150);
  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  await moveCursorTo(cx, cy, duration);
  return true;
}

/** Tıklama pulse animasyonu. */
export async function cursorClick(): Promise<void> {
  useCursorStore.setState({ clicking: true });
  await sleep(220);
  useCursorStore.setState({ clicking: false });
  await sleep(120);
}

export function VoiceCursor() {
  const { visible, x, y, clicking, label } = useCursorStore();
  if (!visible) return null;

  return (
    <div
      className="pointer-events-none fixed z-[9999]"
      style={{
        left: 0,
        top: 0,
        transform: `translate(${x}px, ${y}px)`,
        transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {/* Tıklama halkası */}
      {clicking && (
        <span
          className="absolute -left-3 -top-3 h-10 w-10 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(15,153,253,0.35) 0%, rgba(15,153,253,0) 70%)',
            animation: 'voiceCursorPing 0.4s ease-out',
          }}
        />
      )}
      <MousePointer2
        className="h-7 w-7"
        style={{
          color: '#0F99FD',
          fill: 'rgba(15,153,253,0.25)',
          filter: 'drop-shadow(0 2px 6px rgba(15,153,253,0.5))',
          transform: clicking ? 'scale(0.8)' : 'scale(1)',
          transition: 'transform 180ms ease',
        }}
      />
      {label && (
        <span
          className="absolute left-7 top-5 whitespace-nowrap rounded-md bg-[#0F99FD] px-2 py-1 text-[11px] font-medium text-white shadow-lg"
          style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {label}
        </span>
      )}
      <style
        dangerouslySetInnerHTML={{
          __html: `@keyframes voiceCursorPing { from { transform: scale(0.4); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }`,
        }}
      />
    </div>
  );
}
